/**
 * /thank-you-talent — confirmation + conversion tracking hook.
 *
 * Root element carries data-conversion="talent_signup_completed" so Meta
 * Pixel / Google Ads / GTM can fire the CompleteRegistration event.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Sparkles, MailCheck, IdCard, Zap } from "lucide-react";

const NEXT_STEPS = [
    { icon: Sparkles, title: "Your Talent Passport is being built", body: "Simonara AI is parsing your resume and building your career profile now." },
    { icon: MailCheck, title: "Check your inbox", body: "You'll receive a link to set your password and unlock your AI Career Dashboard." },
    { icon: IdCard, title: "Get discovered", body: "As soon as you finish setup, your passport becomes visible to every hiring employer on the platform." },
];

export default function ThankYouTalent() {
    const [email, setEmail] = useState("");

    useEffect(() => {
        try {
            const stored = sessionStorage.getItem("simonara_signup_email") || "";
            if (stored) setEmail(stored);
        } catch { /* ignore quota */ }

        try {
            if (typeof window !== "undefined" && typeof window.dataLayer !== "undefined") {
                window.dataLayer.push({ event: "talent_signup_completed" });
            }
            if (typeof window !== "undefined" && typeof window.fbq !== "undefined") {
                window.fbq("track", "CompleteRegistration", { content_name: "talent_signup" });
            }
        } catch { /* ignore analytics failures */ }
    }, []);

    return (
        <div
            className="bg-background"
            data-testid="thank-you-talent-page"
            data-conversion="talent_signup_completed"
        >
            <Helmet>
                <title>Your Talent Passport is being built · Simonara</title>
                <meta name="robots" content="noindex" />
                <link rel="canonical" href="/thank-you-talent" />
            </Helmet>

            <section className="container-page py-24 max-w-3xl mx-auto text-center">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                    <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
                        <CheckCircle2 size={32} className="text-emerald-500" />
                    </div>
                    <p className="overline mt-6">Confirmed</p>
                    <h1 className="mt-2 font-heading font-semibold text-4xl sm:text-5xl tracking-tight text-foreground">
                        You're in. Your Talent Passport is being built.
                    </h1>
                    <p className="mt-4 text-lg text-muted-foreground">
                        {email ? (
                            <>We just sent a setup link to <span className="font-medium text-foreground">{email}</span>. Finish creating your password to unlock your AI Career Dashboard.</>
                        ) : (
                            <>Check your inbox — we've sent your setup link. Finish creating your password to unlock your AI Career Dashboard.</>
                        )}
                    </p>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
                        {NEXT_STEPS.map((s) => (
                            <div key={s.title} className="card-soft p-5">
                                <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                                    <s.icon size={16} />
                                </div>
                                <div className="mt-3 font-heading font-semibold text-foreground text-sm">{s.title}</div>
                                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{s.body}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
                        <Link
                            to={`/signup?role=professional${email ? `&email=${encodeURIComponent(email)}` : ""}`}
                            className="btn-green inline-flex items-center gap-2"
                            data-testid="thank-you-talent-signup"
                        >
                            Set your password <ArrowRight size={14} />
                        </Link>
                        <Link
                            to="/careers"
                            className="btn-outline inline-flex items-center gap-2"
                            data-testid="thank-you-talent-jobs"
                        >
                            <Zap size={14} /> Browse open roles
                        </Link>
                    </div>
                </motion.div>
            </section>
        </div>
    );
}
